import React, { useState, useEffect } from "react";
import AdButtonGroup from "../AdButtonGroup";
import { Table, Button, Container, Card, CardBody, CardHeader } from "reactstrap";
import { FaRegSadCry } from "react-icons/fa";
import BreadCrumb from "../../../../components/common/BreadCrumb";
import AdFormModal from "./AdFormModal";
import GoogleAdsModal from "./GoogleAdsModal";
import MetaAdsModal from "./MetaAdsModal";
import TiktokAdsModal from "./TiktokAdsModal";
import { useDispatch, useSelector } from "react-redux";
import { fetchConfigsByType } from "../../../../slices/veloxity/integration/fetchConfigsSlice";
import { deleteConfig } from "../../../../slices/veloxity/integration/deleteConfigSlice";
import { createConfig } from "../../../../slices/veloxity/integration/createConfigSlice";
import { createTiktokConfig } from "../../../../slices/veloxity/integration/createTiktokConfigSlice";
import { updateConfig } from "../../../../slices/veloxity/integration/updateConfigSlice";
import Loader from "../../../../components/common/Loader";
import showConfirmationAlert from "../../../../components/custom/confirmationAlert";
import { Link } from 'react-router-dom';
import { showErrorAlert, showSuccessAlert } from "../../../../components/custom/sweetalertHelper";

function ThirdPartyPlatformsAccount() {
    const [selectedButton, setSelectedButton] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentConfig, setCurrentConfig] = useState(null);

    const dispatch = useDispatch();
    const { loading, data } = useSelector(state => state.fetchConfigs);

    const typeMap = {
        0: "ADFORM_S3",
        1: "GOOGLE",
        2: "FACEBOOK",
        3: "TIKTOK"
    };

    useEffect(() => {
        if (selectedButton in typeMap) {
            dispatch(fetchConfigsByType(typeMap[selectedButton]));
        }
    }, [selectedButton, dispatch]);

    const handleEdit = (config) => {
        setCurrentConfig(config);
        setModalOpen(true);
    };

    const handleDelete = (type, id) => {
        showConfirmationAlert("Are you sure you want to delete this configuration?", () => {
            dispatch(deleteConfig({ type, id })).then((response) => {
                if (!response.error) {
                    showSuccessAlert("Deleted!", "The configuration has been deleted.");
                    dispatch(fetchConfigsByType(typeMap[selectedButton]));
                } else {
                    showErrorAlert("Error!", "There was an issue deleting the configuration.");
                }
            });
        });
    };

    const handleSubmit = async (formData) => {
        let requestBody;

        switch (selectedButton) {
            case 0: // AdForm - S3
                const { name, expireDate, accessKey, secretKey, bucketName, region } = formData;
                requestBody = {
                    name,
                    expireDate,
                    config: {
                        credentials: {
                            accessKey,
                            secretKey
                        },
                        bucketInfo: {
                            name: bucketName,
                            region
                        }
                    }
                };
                break;
            case 1: // Google Ads
                const { clientId, clientSecret, developerToken, refreshToken, mccCustomerId } = formData;
                requestBody = {
                    name: formData.name,
                    expireDate: formData.expireDate,
                    config: {
                        clientId,
                        clientSecret,
                        developerToken,
                        refreshToken,
                        mccCustomerId
                    }
                };
                break;
            case 2: // Meta Ads
                const { appSecret, accessToken } = formData;
                requestBody = {
                    name: formData.name,
                    expireDate: formData.expireDate,
                    config: {
                        appSecret,
                        accessToken,
                        appId: formData.appId
                    }
                };  
                break;
             case 3: // TikTok Ads
                const { tiktokAccessToken } = formData; 
                requestBody = {
                    name: formData.name,
                    expireDate: formData.expireDate,
                    accessToken: tiktokAccessToken 
                };
                break;
            default:
                console.error("Unsupported platform selected");
                return;
        }

        if (currentConfig) {
            await dispatch(updateConfig({ type: typeMap[selectedButton], requestData: { id: currentConfig.id, ...requestBody } }))
            .then((action) => {
                if (action.type.endsWith('fulfilled')) {
                  showSuccessAlert('Success!', `${typeMap[selectedButton]} configuration has been updated successfully.`);
                }
              })
              .catch((error) => {
                showErrorAlert('Error!', error.message);
              })
        } else {   
            await dispatch(createConfig({ type: typeMap[selectedButton], requestData: requestBody }))
            .then((action) => {
                if (action.type.endsWith('fulfilled')) {
                  showSuccessAlert('Success!', `${typeMap[selectedButton]} configuration has been updated successfully.`);
                }
            })
            .catch((error) => {
                showErrorAlert('Error!', error.message);
            });
        }

        dispatch(fetchConfigsByType(typeMap[selectedButton]));
        setModalOpen(false);
    };

    const columnConfigs = {
        "AdForm - S3": ["Name", "Expire Date", "Access Key", "Secret Key", "Bucket Name", "Region", "Action"],
        "Google Ads": ["Name", "Expire Date", "Client Id", "Client Secret", "Developer Token", "Refresh Token", "MCC Customer Id", "Action"],
        "Meta Ads": ["Name", "Expire Date", "App Secret", "Access Token", "Action"],
        "TikTok Ads": ["Name", "Expire Date", "Access Token","Action"],
        "LinkedIn Ads": ["Coming Soon"],
        "Amazon Ads": ["Coming Soon"]
    };

    const getTableColumns = () => {
        if (selectedButton === null) return [];
        const selectedPlatform = Object.keys(columnConfigs)[selectedButton];
        return columnConfigs[selectedPlatform] || [];
    };

    const toggleModal = () => {
        setModalOpen(!modalOpen);

        if (!modalOpen) {
            setCurrentConfig(null);
        }
    };

    const renderTable = () => {
        const columns = getTableColumns();

        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
                    <Loader />
                </div>
            );
        }

        return (
            <Card>
                <CardHeader>
                    <div className="d-flex justify-content-end align-items-center">
                        <Button color="primary" onClick={toggleModal}>New Account</Button>
                    </div>
                </CardHeader>
                <CardBody style={{ height: '400px', overflowY: 'auto' }}>
                    <div className="table-responsive">
                        <Table
                            className="table-hover table-striped align-middle table-nowrap mb-0"
                            style={{ tableLayout: 'fixed', width: '100%' }} // Sabit genişlik ve tablo düzenini ayarlıyoruz
                        >
                            <thead>
                                <tr>
                                    {columns.map((col, index) => (
                                        <th
                                            key={index}
                                            style={col === "Action" ? { width: '150px' } : {
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.length > 0 ? (
                                    data.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            <td style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>{row.name}</td>
                                            <td style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>{row.expireDate}</td>
                                            {selectedButton === 0 && (
                                                <>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.credentials?.accessKey || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.credentials?.secretKey || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.bucketInfo?.name || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.bucketInfo?.region || 'N/A'}</td>
                                                </>
                                            )}
                                            {selectedButton === 1 && (
                                                <>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.clientId || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.clientSecret || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.developerToken || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.refreshToken || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.mccCustomerId || 'N/A'}</td>
                                                </>
                                            )}
                                            {selectedButton === 2 && (
                                                <>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.appSecret || 'N/A'}</td>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.accessToken || 'N/A'}</td>
                                                </>
                                            )}

                                            {selectedButton === 3 && (
                                                <>
                                                    <td style={{
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>{row.metaData?.accessToken || 'N/A'}</td>
                                                </>
                                            )}
                                            <td className="action-column">
                                                <Link to="#">
                                                    <i
                                                        className="ri-edit-line fs-17 lh-1 align-middle text-primary"
                                                        style={{ marginRight: '10px', cursor: 'pointer' }}
                                                        onClick={() => handleEdit(row)}
                                                    ></i>
                                                </Link>
                                                <Link to="#">
                                                    <i
                                                        className="ri-delete-bin-line fs-17 lh-1 align-middle text-danger"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleDelete(typeMap[selectedButton], row.id)}
                                                    ></i>
                                                </Link>
                                            </td>

                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length} className="text-center">
                                            <FaRegSadCry size={50} color="gray" />
                                            <h4 className="mt-3">No Data Available</h4>
                                            <p className="text-muted">It looks like you haven't added any accounts yet. Start by adding a new account.</p>
                                            <Button color="primary" onClick={toggleModal}>New Account</Button>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
        );
    };

    const handleButtonSelect = (buttonIndex) => {
        setSelectedButton(buttonIndex);
    };

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Account Management" pageTitle="3rd Party Platforms" />
                <div style={{ width: "115%", marginLeft: '-7.5%' }}>
                    <AdButtonGroup onButtonSelect={handleButtonSelect} selectedButton={selectedButton} />
                </div>

                {selectedButton !== null ? (
                    <>
                        {renderTable()}

                        {selectedButton === 0 && (
                            <AdFormModal
                                isOpen={modalOpen}
                                toggle={toggleModal}
                                currentConfig={currentConfig}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {selectedButton === 1 && (
                            <GoogleAdsModal
                                isOpen={modalOpen}
                                toggle={toggleModal}
                                currentConfig={currentConfig}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {selectedButton === 2 && (
                            <MetaAdsModal
                                isOpen={modalOpen}
                                toggle={toggleModal}
                                currentConfig={currentConfig}
                                onSubmit={handleSubmit}
                            />
                        )}

                        {selectedButton === 3 && (
                            <TiktokAdsModal
                                isOpen={modalOpen}
                                toggle={toggleModal}
                                currentConfig={currentConfig}
                                onSubmit={handleSubmit}
                            />
                        )}
                    </>
                ) : (
                    <p className="text-danger" style={{ textAlign: "center" }}>You do not have access to any platform. If you believe this is an error, please contact us at: csm@veloxity.net</p>
                )}

            </Container>
        </div>
    );
}

export default ThirdPartyPlatformsAccount;